import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardSectionHeader,
  dashboardDataTable as dt,
} from "src/components/common";
import RestaurantAiExecutiveSummary from "../RestaurantAiExecutiveSummary";
import RestaurantHeaderActions, { type RestaurantHeaderActionsProps } from "../RestaurantHeaderActions";
import RestaurantKpiCard from "../RestaurantKpiCard";
import RestaurantSectionCard from "../RestaurantSectionCard";
import RestaurantBranchMap from "../RestaurantBranchMap";
import RestaurantHorizontalBars from "../charts/RestaurantHorizontalBars";
import { BRANCHES } from "../../data/restaurantDemoData";
import { useRestaurantFormatters } from "../../utils/restaurantFormatters";
import type { RestaurantFilters, RestaurantMetrics } from "../../utils/restaurantMetrics";

interface RestaurantBranchesTabProps {
  metrics: RestaurantMetrics;
  filters: RestaurantFilters;
  headerProps: RestaurantHeaderActionsProps;
}

const RestaurantBranchesTab: FC<RestaurantBranchesTabProps> = ({ metrics, filters, headerProps }) => {
  const { t, i18n } = useTranslation();
  const fmt = useRestaurantFormatters();
  const isRTL = i18n.language.toLowerCase().startsWith("ar");

  const branchInfo = (id: string) => BRANCHES.find(b => b.id === id);
  const branchLabel = (id: string) => {
    const info = branchInfo(id);
    return info ? (isRTL ? info.nameAr : info.name) : id;
  };

  const leaderboardBars = metrics.branches.map(branch => ({
    id: branch.id,
    label: branchLabel(branch.id),
    value: branch.revenue,
  }));

  const bestBranch = metrics.branches[0];
  const totalRevenue = metrics.branches.reduce((sum, b) => sum + b.revenue, 0) || 1;

  return (
    <div className="restaurant-analytics-dashboard__section">
      <DashboardSectionHeader
        title={t("restaurantAnalytics.tabs.branches", { defaultValue: "Branches" })}
        subtitle={t("restaurantAnalytics.header.branchesSubtitle", {
          defaultValue: "Compare revenue, fulfilment, and location performance.",
        })}
        sticky
        actions={<RestaurantHeaderActions {...headerProps} />}
      />

      <RestaurantAiExecutiveSummary tab="branches" metrics={metrics} filters={filters} />

      <div className="restaurant-analytics-dashboard__kpi-row">
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.activeBranches", { defaultValue: "Active branches" })}
          value={fmt.formatNumber(metrics.activeBranches)}
          accent="primary"
          tooltip={t("restaurantAnalytics.kpiTooltips.activeBranches", { defaultValue: "Number of branches included in the current filter selection." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.topBranchByRevenue", { defaultValue: "Top branch by revenue" })}
          value={bestBranch ? branchLabel(bestBranch.id) : "—"}
          accent="success"
          tooltip={t("restaurantAnalytics.kpiTooltips.topBranchByRevenue", { defaultValue: "The branch generating the most revenue in the period." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.avgOrderValue", { defaultValue: "Average order value" })}
          value={fmt.formatCurrencyAverage(metrics.aov)}
          delta={metrics.aovDelta}
          accent="warning"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.avgOrderValue", { defaultValue: "Total revenue divided by total orders." })}
        />
        <RestaurantKpiCard
          label={t("restaurantAnalytics.kpi.avgFulfilmentTime", { defaultValue: "Avg. fulfilment time" })}
          value={fmt.formatMinutes(metrics.fulfilmentMinutes)}
          delta={metrics.fulfilmentMinutesDelta}
          accent="primary"
          tooltipAlign="end"
          tooltip={t("restaurantAnalytics.kpiTooltips.avgFulfilmentTime", { defaultValue: "Average time from order placed to order fulfilled." })}
        />
      </div>

      <div className="restaurant-analytics-dashboard__grid restaurant-analytics-dashboard__grid--branches">
        <div className="restaurant-analytics-dashboard__card--span-three">
          <RestaurantBranchMap metrics={metrics} />
        </div>
        <div className="restaurant-analytics-dashboard__card--span-two">
          <RestaurantHorizontalBars
            title={t("restaurantAnalytics.charts.branchLeaderboard", { defaultValue: "Branch leaderboard" })}
            tip={t("restaurantAnalytics.chartTips.branchLeaderboard", { defaultValue: "Branches ranked by revenue for the selected period and filters." })}
            data={leaderboardBars}
            valueKind="currency"
          />
        </div>
      </div>

      <RestaurantSectionCard
        title={t("restaurantAnalytics.charts.branchComparison", { defaultValue: "Branch comparison" })}
        tip={t("restaurantAnalytics.chartTips.branchComparison", { defaultValue: "Side-by-side revenue, orders, prep time, rating, and capacity per branch." })}
        bodyClassName="restaurant-table-body"
      >
        <DashboardDataTableScroll>
          <DashboardDataTableTable className={dt.tableCompact}>
            <thead>
              <tr>
                <th className={dt.number}>#</th>
                <th>{t("restaurantAnalytics.table.name", { defaultValue: "Name" })}</th>
                <th>{t("restaurantAnalytics.table.city", { defaultValue: "City" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.revenue", { defaultValue: "Revenue" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.orders", { defaultValue: "Orders" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.aov", { defaultValue: "AOV" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.revenueShare", { defaultValue: "Revenue share" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.avgPrepTime", { defaultValue: "Avg. prep time" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.rating", { defaultValue: "Rating" })}</th>
                <th className={dt.number}>{t("restaurantAnalytics.table.seats", { defaultValue: "Seats" })}</th>
              </tr>
            </thead>
            <tbody>
              {metrics.branches.map((branch, index) => {
                const info = branchInfo(branch.id);
                if (!info) {return null;}
                return (
                  <tr key={branch.id}>
                    <td className={dt.number}>{fmt.formatNumber(index + 1)}</td>
                    <td className={dt.cellEmphasis}>{isRTL ? info.nameAr : info.name}</td>
                    <td>{isRTL ? info.cityAr : info.city}</td>
                    <td className={dt.number}>{fmt.formatCurrency(branch.revenue, { compact: true })}</td>
                    <td className={dt.number}>{fmt.formatNumber(branch.orders)}</td>
                    <td className={dt.number}>{fmt.formatCurrencyAverage(branch.aov)}</td>
                    <td className={dt.number}>{fmt.formatPercent(branch.revenue / totalRevenue)}</td>
                    <td className={dt.number}>{fmt.formatMinutes(info.avgPrepMinutes, 0)}</td>
                    <td className={dt.number}>{fmt.formatNumber(info.rating, 1)}</td>
                    <td className={dt.number}>{fmt.formatNumber(info.seats)}</td>
                  </tr>
                );
              })}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
      </RestaurantSectionCard>
    </div>
  );
};

export default RestaurantBranchesTab;
